import {
    BoxOrder,
    PurchaseEventData,
    WipInvoicingDetails
} from 'src/business/ordering/order/BoxOrder'
import { createPersistentStore } from '@core'
import { Store } from '@core/types'
import { ClientUser } from 'src/api/defs/ClientUser.type'
import { Country, InvoicingDetails$Type } from 'src/api/defs/enums.type'

const invoiceDetailsFromUser = (
    u: ClientUser,
    version: number
): WipInvoicingDetails => ({
    version,
    firstName: u.firstName,
    lastName: u.lastName,
    email: u.email,
    personalId: u.personalId,
    telephoneNumber: u.phoneNumber,
    company: u.company || null,
    address: u.address,
    type: u.company
        ? InvoicingDetails$Type.PravnickaOsoba
        : InvoicingDetails$Type.FyzickaOsoba
})

export class OrderStore implements Store<BoxOrder | null> {
    private store: Store<BoxOrder | null> = (() => {
        const s = createPersistentStore<BoxOrder | null>(
            window.localStorage,
            'order',
            null
        )

        const current = s.value()
        const createdAt = current?.createdAt
        const hasValidCreatedAt =
            createdAt && !Number.isNaN(new Date(createdAt).getTime())

        if (current && !hasValidCreatedAt) {
            s.change({
                ...current,
                createdAt: new Date().toISOString()
            })
            console.debug('[OrderStore] migrated order – added createdAt')
        }

        return s
    })()

    startNew = (
        boxId: number,
        warehouseId: number,
        country: Country,
        warehouseCity: string
    ) => {
        this.change({
            box: boxId,
            warehouseId,
            warehouseCity,
            country,
            insurance: null,
            invoicing: null,
            paymentFailed: false,
            invoicingDetails: null,
            invoicingDetailsValid: false,
            invoicingDetailsValidationPending: false,
            voucherToken: null,
            forceValidate: null,
            version: 0,
            createdAt: new Date().toISOString(),
            gdprConsent: false,
            vopConsent: false,
            recurrentPaymentConsent: false
        })
    }

    prefillOrderFromUser = (user: ClientUser): void => {
        const current = this.value()?.invoicingDetails
        const version = current ? current.version + 1 : 0
        this.cp({
            invoicingDetails: invoiceDetailsFromUser(user, version)
        })
    }

    setEmptyInvoicingDetails = (): void => {
        const current = this.value()?.invoicingDetails
        const version = current ? current.version + 1 : 0

        this.setInvoicingDetails({
            version,
            company: null,
            type: InvoicingDetails$Type.FyzickaOsoba
        })
    }

    setInvoicingDetails = (d: WipInvoicingDetails) =>
        this.cp({
            invoicingDetails: d,
            invoicingDetailsValidationPending: true
        })

    setInvoicingDetailsValid = (valid: boolean) =>
        this.cp({
            invoicingDetailsValid: valid,
            invoicingDetailsValidationPending: false
        })

    setInvoicingDetailsValidationPending = (pending: boolean) =>
        this.cp({
            invoicingDetailsValidationPending: pending
        })

    setForceValidate = (force: BoxOrder['forceValidate']) =>
        this.cp({
            forceValidate: force
        })

    setOrderVersion = (version: number) =>
        this.cp({
            version
        })

    setInvoicing = (invoicing: number) =>
        this.cp({
            invoicing
        })

    setInsurance = (insurance: number | null) =>
        this.cp({
            insurance
        })

    setPaymentFailed = () =>
        this.cp({
            paymentFailed: true
        })

    clear = () => {
        this.change(null)
    }

    private cp = (changes: Partial<BoxOrder>): void => {
        const o = this.store.value()

        if (!o) throw new Error('Cannot change order. Is not set.')
        this.store.change({
            ...o,
            ...changes
        })
        console.log('[OrderStore] Order changed:', this.store.value())
    }

    setVoucherToken = (voucherToken: string | null) =>
        this.cp({
            voucherToken
        })

    setGdprConsent = (consent: boolean) =>
        this.cp({
            gdprConsent: consent
        })

    setVopConsent = (consent: boolean) =>
        this.cp({
            vopConsent: consent
        })

    setRecurrentPaymentConsent = (consent: boolean) =>
        this.cp({
            recurrentPaymentConsent: consent
        })

    setPurchaseEventData = (purchaseEventData: PurchaseEventData) =>
        this.cp({ purchaseEventData })

    change = (t: BoxOrder | null) => this.store.change(t)

    changes = () => this.store.changes()

    value = () => this.store.value()

    getData = () => this.value()?.invoicingDetails
}
